import SideSheetModal from "@/components/SideSheetModal";
import { useQuery } from "@apollo/client";
import { FACILITY_DETAILS_CONTACT_QUERY, FacilityDetailsContactQueryResponse } from "@/features/facility/queries";
import { PERSON_DETAILS_QUERY } from "@/pages/people/[pgaId]/person/queries";
import { useNavigate } from "@/router";
import FacilitySideSheetContent from "./FacilitySideSheetContent";
import FacilitySideSheetSkeleton from "./FacilitySideSheetSkeleton";
import { MaterialNames } from "@/components/material_symbol_names";
import EmptyState from "@/components/EmptyState";

export interface CustomActionButton {
  text: string;
  variant?: "text" | "outlined" | "filled";
  onClick: () => void;
  testId?: string;
  iconName?: MaterialNames;
}
import {
  EmploymentFacilityQueryResponse,
  GET_EMPLOYMENT_FACILITY_DETAILS_QUERY
} from "@/pages/people/[pgaId]/person/queries";

export type FacilitySideSheetProps = {
  open: boolean;
  onClose: () => void;
  facilityId?: string | null;
  employmentId?: string;
  pgaId?: string; // falls back to primary employment → facility
  customActionButtons?: CustomActionButton[];
};

export default function FacilitySideSheet({
  open,
  onClose,
  facilityId,
  employmentId,
  pgaId,
  customActionButtons
}: FacilitySideSheetProps) {
  const navigate = useNavigate();

  // If pgaId is provided, fetch primary employment id
  const { data: personData } = useQuery(PERSON_DETAILS_QUERY, {
    variables: { pgaId },
    skip: !open || !pgaId || !!employmentId || !!facilityId
  });

  const resolvedEmploymentId = employmentId || personData?.person?.employments?.find((e: any) => e.isPrimary)?.id;

  // If facilityId is provided, fetch facility directly
  const {
    data: facilityData,
    loading: facilityLoading,
    error: facilityError
  } = useQuery<FacilityDetailsContactQueryResponse>(FACILITY_DETAILS_CONTACT_QUERY, {
    variables: { facilityId },
    skip: !open || !facilityId
  });

  // Else fetch via employmentId
  const {
    data: employmentData,
    loading: employmentLoading,
    error: employmentError
  } = useQuery<EmploymentFacilityQueryResponse>(GET_EMPLOYMENT_FACILITY_DETAILS_QUERY, {
    variables: { employmentId: resolvedEmploymentId },
    skip: !open || !!facilityId || !resolvedEmploymentId
  });

  function setIsSideSheetOpen(isOpen: boolean) {
    if (!isOpen) onClose();
  }

  const facility = facilityData?.facility || employmentData?.employment?.facility;

  const operatingStatusOptions = facilityData?.operatingStatusOptions || employmentData?.operatingStatusOptions;
  const facilityTypeOptions = facilityData?.facilityTypeOptions || employmentData?.facilityTypeOptions;
  const operationTypeOptions = facilityData?.operationTypeOptions || employmentData?.operationTypeOptions;

  const loading = (!!facilityId && facilityLoading) || (!!resolvedEmploymentId && employmentLoading);
  const error = facilityError || employmentError;

  return (
    <SideSheetModal isSidebarOpen={open} setSidebarOpen={setIsSideSheetOpen} title="Facility Details" actions={null}>
      {loading ? (
        <FacilitySideSheetSkeleton />
      ) : error ? (
        <div className="p-6">Error: {error.message}</div>
      ) : facility ? (
        <FacilitySideSheetContent
          facility={facility as any}
          renderedLabels={{
            operatingStatus: operatingStatusOptions?.enumValues?.find((e) => e.name === facility?.operatingStatus)
              ?.description,
            facilityType: facilityTypeOptions?.enumValues?.find((e) => e.name === facility?.facilityType)?.description,
            operationType: operationTypeOptions?.enumValues?.find((e) => e.name === facility?.operationType)
              ?.description
          }}
          setIsSideSheetOpen={setIsSideSheetOpen}
          navigate={navigate}
          customActionButtons={customActionButtons}
        />
      ) : (
        <EmptyState iconName="location_on" title="Facility Not Found" />
      )}
    </SideSheetModal>
  );
}
